<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'

const props = defineProps<{
  open: boolean
  symbol: string
  durationMs?: number
}>()

const emit = defineEmits<{
  close: []
}>()

const AUTO_CLOSE_MS = 15000
const progress = ref(100)
let rafId = 0
let startedAt = 0

function stopTimer() {
  if (rafId) {
    cancelAnimationFrame(rafId)
    rafId = 0
  }
}

function tick() {
  const total = props.durationMs ?? AUTO_CLOSE_MS
  const elapsed = performance.now() - startedAt
  const remaining = Math.max(0, 1 - elapsed / total)
  progress.value = remaining * 100

  if (remaining <= 0) {
    stopTimer()
    emit('close')
    return
  }

  rafId = requestAnimationFrame(tick)
}

function startTimer() {
  stopTimer()
  startedAt = performance.now()
  progress.value = 100
  rafId = requestAnimationFrame(tick)
}

watch(
  () => props.open,
  (open) => {
    if (open) startTimer()
    else stopTimer()
  },
  { immediate: true },
)

onBeforeUnmount(stopTimer)

const secondsLeft = computed(() =>
  Math.max(1, Math.ceil((progress.value / 100) * ((props.durationMs ?? AUTO_CLOSE_MS) / 1000))),
)
</script>

<template>
  <Teleport to="body">
    <Transition name="liq-toast">
      <aside
        v-if="open"
        dir="ltr"
        class="liq-toast"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <div class="liq-toast__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
            <path
              d="M12 3v10m0 0 4-4m-4 4-4-4M5 21h14"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <div class="liq-toast__content">
          <div class="liq-toast__row">
            <p class="liq-toast__title">Price zones updated</p>
            <button
              type="button"
              class="liq-toast__close"
              aria-label="Close"
              @click="emit('close')"
            >
              <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
                <path
                  d="M4 4l8 8M12 4l-8 8"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </div>
          <p class="liq-toast__body">
            <span class="liq-toast__symbol">{{ symbol }}</span>
            levels refreshed on chart and table.
          </p>
          <div class="liq-toast__footer">
            <div class="liq-toast__bar" aria-hidden="true">
              <div class="liq-toast__bar-fill" :style="{ width: `${progress}%` }" />
            </div>
            <span class="liq-toast__timer">{{ secondsLeft }}s</span>
          </div>
        </div>
      </aside>
    </Transition>
  </Teleport>
</template>

<style scoped>
.liq-toast {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 9999;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  width: min(calc(100vw - 2rem), 22rem);
  padding: 0.875rem 1rem;
  border-radius: 0.75rem;
  border: 1px solid #363a45;
  background: #1e222d;
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.45),
    0 0 0 1px rgba(255, 255, 255, 0.04) inset;
  color: #d1d4dc;
  font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;
  text-align: left;
  direction: ltr;
  unicode-bidi: isolate;
}
.liq-toast__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  background: rgba(38, 166, 154, 0.15);
  color: #26a69a;
}
.liq-toast__content {
  flex: 1;
  min-width: 0;
}
.liq-toast__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}
.liq-toast__title {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: #f0f3fa;
}
.liq-toast__close {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: #787b86;
  cursor: pointer;
}
.liq-toast__close:hover {
  background: #2a2e39;
  color: #d1d4dc;
}
.liq-toast__body {
  margin: 0 0 0.625rem;
  font-size: 0.75rem;
  line-height: 1.45;
  color: #787b86;
}
.liq-toast__symbol {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-weight: 600;
  color: #2962ff;
}
.liq-toast__footer {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.liq-toast__bar {
  flex: 1;
  height: 3px;
  border-radius: 9999px;
  background: #2a2e39;
  overflow: hidden;
}
.liq-toast__bar-fill {
  height: 100%;
  border-radius: inherit;
  background: #2962ff;
  transition: width 0.05s linear;
}
.liq-toast__timer {
  flex-shrink: 0;
  font-size: 0.6875rem;
  font-variant-numeric: tabular-nums;
  color: #787b86;
  min-width: 1.5rem;
  text-align: right;
}
.liq-toast-enter-active,
.liq-toast-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.liq-toast-enter-from,
.liq-toast-leave-to {
  opacity: 0;
  transform: translateX(12px);
}
</style>
